import { useCallback } from 'react';
import { useSnackbar } from '../context/snackbarContext';
import { useTranslation } from 'react-i18next';

/**
 * Centralized API error handler hook that provides consistent error handling
 * across all API operations with user-friendly translated messages.
 */
export const useApiErrorHandler = () => {
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const handleApiError = useCallback((error: any, context?: string) => {
        console.error(`API Error${context ? ` (${context})` : ''}:`, error);

        // Extract error information
        const apiError = error?.response?.data;
        const statusCode = error?.response?.status;
        const errorType = apiError?.errorType;
        const errorCode = apiError?.code;
        const errorMessage = apiError?.message;

        // Determine error category and message
        let userMessage: string;

        // 1. Handle image generation errors (regardless of context)
        if (errorType && ['safety_filter', 'api_server_error', 'server_error'].includes(errorType)) {
            const translationKey = `errors.imageGeneration.${errorType}`;
            userMessage = t(translationKey, {
                defaultValue: t('errors.imageGeneration.unknown')
            });
        }
        // 2. Handle specific error codes (like style resolution errors)
        else if (errorCode) {
            const translationKey = `errors.styleResolution.${errorCode}`;
            userMessage = t(translationKey, {
                defaultValue: t('errors.general.unknown')
            });
        }
        // 3. Handle HTTP status codes
        else if (statusCode) {
            const translationKey = `errors.http.${statusCode}`;
            userMessage = t(translationKey, {
                defaultValue: t('errors.general.unknown')
            });
        }
        // 4. Handle network errors
        else if (error?.message?.includes('Network Error') || error?.code === 'ERR_NETWORK') {
            userMessage = t('errors.general.network', {
                defaultValue: t('errors.networkError', { defaultValue: 'Network error. Please check your connection.' })
            });
        }
        // 5. Handle timeout errors
        else if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
            userMessage = t('errors.general.timeout', {
                defaultValue: t('errors.general.unknown', { defaultValue: 'Request timed out. Please try again.' })
            });
        }
        // 6. Fallback to general unknown error
        else {
            userMessage = t('errors.general.unknown');
        }

        showSnackbar(userMessage, 'error');
    }, [showSnackbar, t]);

    return { handleApiError };
};
